<template>
  <SimpleTable
    ref="tableRef"
    v-bind="$attrs"
    :headers="orderedTableHeaders"
    :data="records"
    :pagination="pagination"
    :show-actions-column="showActionsColumn"
    :actions="actions"
    :custom-row="customRow"
    :custom-column-render="enhancedCustomColumnRender"
    :per-page-options="perPageOptions"
    :row-selection="rowSelection"
    :selection="selection"
    :force-x-virtual="forceXVirtual"
    :auto-height="false"
    @update:pagination="handlePageChange"
    @update:selection="handleSelectionChange"
    @refresh="$emit('refresh')"
    @update:columns="handleColumnsUpdate"
    @update:column-width="handleColumnWidthUpdate"
    :height="height"
  >
    <template #bulk-actions>
      <slot name="bulk-actions" />
    </template>

    <template #headerCell="{ column }">
      <div
        v-if="column.key === 'add_property'"
        class="flex items-center justify-center"
      >
        <AddPropertyHeaderDropdown
          :entity-type="entityType"
          :project-id="projectId"
          :service="service"
          @property-added="$emit('property-added', $event)"
        />
      </div>

      <div
        v-else-if="column.key !== '_row_actions'"
        class="custom_header_wrapper truncate"
        :class="{ 'is-draggable': !column.fixed }"
      >
        <div class="flex items-center relative flex-1">
          <ColumnHeaderDropdown
            :ref="setHeaderDropdownRef(column.key)"
            :column="column"
            :property-meta="getPropertyMeta(column.key)"
            :entity-type="entityType"
            :project-id="projectId"
            :is-dynamic-object="isDynamicObject"
            :show-column-edit="showColumnEdit"
            :current-sorts="sortData"
            :frozen-columns="frozenColumns"
            :all-columns="columns"
            :number-attributes="numberAttributes"
            :service="service"
            @sort="$emit('column-sort', $event)"
            @delete-property="$emit('delete-property', $event)"
            @property-updated="$emit('property-updated', $event)"
            @delete-option="$emit('delete-option', $event)"
            @current-sort-changed="handleCurrentSortChanged"
            @freeze-column="$emit('freeze-column', $event)"
            @unfreeze-column="$emit('unfreeze-column', $event)"
          >
            <div
              class="flex-1 flex items-center gap-2"
              @click.stop="openHeaderDropdown(column)"
            >
              <span>{{ column.title }}</span>
              <ThemeImage
                v-if="columnSortDirections[column.key] === 'asc'"
                :icon="imageNames.SORT_ASCENDING_NEUTRAL"
                alt="sort_ascending"
                class="size-3"
              />
              <ThemeImage
                v-else-if="columnSortDirections[column.key] === 'desc'"
                :icon="imageNames.SORT_DESCENDING_NEUTRAL"
                alt="sort_descending"
                class="size-3"
              />
            </div>
          </ColumnHeaderDropdown>
        </div>
      </div>
      <span v-else>{{ column.title }}</span>
    </template>
  </SimpleTable>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  excludedFormFields,
  COPYABLE_TYPES,
  isReadonlySchema,
} from "@/src/constants/crmConstants.js";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import AddPropertyHeaderDropdown from "@/src/components/custom_object_table_components/AddPropertyHeaderDropdown.vue";
import ColumnHeaderDropdown from "@/src/components/custom_object_table_components/ColumnHeaderDropdown.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CellWithCopy from "@/src/views/dynamic_crm/CellWithCopy.vue";

const props = defineProps({
  records: {
    type: Array,
    required: true,
  },
  tableHeaders: {
    type: Array,
    required: true,
  },
  columns: {
    type: Array,
    default: () => [],
  },
  pagination: {
    type: Object,
    required: true,
  },
  actions: {
    type: Array,
    required: true,
  },
  customRow: {
    type: Function,
    default: null,
  },
  customColumnRender: {
    type: Object,
    default: () => ({}),
  },
  entityType: {
    type: String,
    required: true,
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  schemaObjectData: {
    type: Object,
    default: null,
  },
  sortData: {
    type: Object,
    default: () => ({}),
  },
  frozenColumns: {
    type: Array,
    default: () => [],
  },
  height: {
    type: String,
    default: "calc(100vh - (280px + var(--banner-height)))",
  },
  perPageOptions: {
    type: Array,
    default: () => [10, 20, 50, 100],
  },
  selection: {
    type: Object,
    default: null,
  },
  showActionsColumn: {
    type: Boolean,
    default: true,
  },
  forceXVirtual: {
    type: Boolean,
    default: true,
  },
  isDynamicObject: {
    type: Boolean,
    default: true,
  },
  showColumnEdit: {
    type: Boolean,
    default: true,
  },
  service: {
    type: String,
    default: null,
  },
});

const emit = defineEmits([
  "update:pagination",
  "column-order-changed",
  "column-width-changed",
  "refresh",
  "property-added",
  "column-sort",
  "delete-property",
  "property-updated",
  "delete-option",
  "sort-changed",
  "freeze-column",
  "unfreeze-column",
  "selection-change",
]);

const columnOrder = ref([]);
const columnSortDirections = ref({});
const headerDropdownRefs = ref({});
const tableRef = ref(null);
const stopWatchers = [];

const rowSelection = computed(() => {
  if (props.selection === null) return null;
  return {};
});

const MAX_VISIBLE_COLUMNS = 50;

const columnsSet = computed(() => new Set(props.columns || []));

const filteredTableHeaders = computed(() => {
  const tableHeaders = props.tableHeaders;
  if (!tableHeaders || tableHeaders.length === 0) return [];

  const colSet = columnsSet.value;
  const hideAddProperty = isReadonlySchema(props.entityType, props.service);

  if (colSet.size === 0) {
    return hideAddProperty
      ? tableHeaders.filter((h) => h.key !== "add_property")
      : tableHeaders;
  }

  let addPropertyHeader = null;
  const regularHeaders = [];

  for (const header of tableHeaders) {
    if (header.key === "add_property") {
      addPropertyHeader = header;
    } else if (colSet.has(header.key)) {
      regularHeaders.push(header);
    }
  }

  const limitedHeaders =
    regularHeaders.length > MAX_VISIBLE_COLUMNS
      ? regularHeaders.slice(0, MAX_VISIBLE_COLUMNS)
      : regularHeaders;

  return addPropertyHeader && !hideAddProperty
    ? limitedHeaders.concat(addPropertyHeader)
    : limitedHeaders;
});

const orderedTableHeaders = computed(() => {
  const headers = filteredTableHeaders.value;
  if (!headers || headers.length === 0) return [];

  const order = columnOrder.value;

  if (!order || order.length === 0) {
    return headers;
  }

  let addPropertyHeader = null;
  const headersMap = new Map();

  for (const h of headers) {
    if (h.key === "add_property") {
      addPropertyHeader = h;
    } else {
      headersMap.set(h.key, h);
    }
  }

  const orderSet = new Set(order);
  const result = [];

  for (const key of order) {
    const header = headersMap.get(key);
    if (header) result.push(header);
  }

  for (const h of headers) {
    if (h.key !== "add_property" && !orderSet.has(h.key)) {
      result.push(h);
    }
  }

  if (addPropertyHeader) {
    result.push(addPropertyHeader);
  }

  return result;
});

// Enhanced custom column render that adds copy on cell hover for copyable columns
const enhancedCustomColumnRender = computed(() => {
  const baseRender = props.customColumnRender || {};
  const headers = orderedTableHeaders.value;

  if (!headers || headers.length === 0) return baseRender;

  const result = { ...baseRender };
  const metaMap = propertyMetaMap.value;

  for (const header of headers) {
    const key = header.key;
    if (key === "add_property") continue;
    if (baseRender[key]) continue;
    if (header.avatar || header.prefix || header.multiType) continue;

    const propertyMeta = metaMap.get(key);
    const datatype = propertyMeta?.datatype;
    if (datatype && !COPYABLE_TYPES.has(datatype)) continue;

    result[key] = {
      component: CellWithCopy,
      props: (record) => ({
        value: record[key],
        attributeType: datatype,
      }),
    };
  }

  return result;
});

const propertyMetaMap = computed(() => {
  const properties = props.schemaObjectData?.properties;
  if (!properties || properties.length === 0) return new Map();
  return new Map(properties.map((p) => [p.slug, p]));
});

const getPropertyMeta = (key) => {
  return propertyMetaMap.value.get(key) || null;
};

// Pre-filter number attributes with Set for excluded fields
const excludedFieldsSet = new Set(excludedFormFields);

const numberAttributes = computed(() => {
  const properties = props.schemaObjectData?.properties;
  if (!properties || properties.length === 0) return [];

  const result = [];
  for (const p of properties) {
    if (
      (p.datatype === "number" || p.datatype === "currency") &&
      !excludedFieldsSet.has(p.slug)
    ) {
      result.push(p);
    }
  }
  return result;
});

const setHeaderDropdownRef = (key) => (el) => {
  if (el) headerDropdownRefs.value[key] = el;
};

const openHeaderDropdown = (column) => {
  const ref = headerDropdownRefs.value[column.key];
  if (ref && ref.toggleDropdown) ref.toggleDropdown();
};

const handleCurrentSortChanged = (data) => {
  if (data.direction) {
    columnSortDirections.value[data.column.key] = data.direction;
  } else {
    delete columnSortDirections.value[data.column.key];
  }
  emit("sort-changed", { columnSortDirections: columnSortDirections.value });
};

const initializeColumnSortDirections = () => {
  columnSortDirections.value = {};
  Object.values(props.sortData).forEach((sort) => {
    if (sort.field && sort.order) {
      columnSortDirections.value[sort.field] = sort.order;
    }
  });
};

const handlePageChange = (newPagination) => {
  emit("update:pagination", newPagination);
};

const handleSelectionChange = (payload) => {
  emit("selection-change", payload);
};

const handleColumnsUpdate = (payload) => {
  if (payload.type === "reorder") {
    columnOrder.value = payload.columnKeys;
    emit("column-order-changed", {
      columnOrder: payload.columnKeys,
    });
  }
};

stopWatchers.push(
  watch(
    () => props.sortData,
    () => {
      initializeColumnSortDirections();
    },
    { deep: true, immediate: true },
  ),
);

const scrollTo = (position, behavior = "auto") => {
  if (tableRef.value?.scrollTo) {
    tableRef.value.scrollTo(position, behavior);
  }
};

const scrollToRight = (behavior = "smooth") => {
  if (tableRef.value?.scrollToRight) {
    tableRef.value.scrollToRight(behavior);
  }
};

defineExpose({
  columnOrder,
  columnSortDirections,
  tableRef,
  scrollTo,
  scrollToRight,
});

onBeforeUnmount(() => {
  // Stop all watchers
  stopWatchers.forEach((stop) => stop());
  stopWatchers.length = 0;

  // Clear header dropdown refs to prevent memory leaks
  headerDropdownRefs.value = {};

  // Clear column order and sort directions
  columnOrder.value = [];
  columnSortDirections.value = {};
});

const handleColumnWidthUpdate = (payload) => {
  if (!payload?.key || typeof payload.width !== "number") return;
  emit("column-width-changed", payload);
};
</script>

<style scoped>
.custom_header_wrapper {
  display: flex;
  align-items: center;
  user-select: none;
  transition: all 0.2s ease;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 8px 12px;
  gap: 4px;
}

.custom_header_wrapper.is-draggable {
  cursor: grab;
}

.custom_header_wrapper.is-draggable:active {
  cursor: grabbing;
}

.custom_header_wrapper:hover {
  background-color: var(--white-opacity-4);
}
</style>
